import type { Json } from "../types";

export interface EngagementTracker {
  /** Active (visible + non-idle) milliseconds accumulated on the current page so far. */
  getEngagedMs(): number;
  destroy(): void;
  emitNow(reason: "visibility" | "spa"): void;
  reset(): void;
}

export interface EngagementTrackerOptions {
  emit: (eventName: string, props: Record<string, Json>) => void;
  getMaxScrollDepth: () => number;
}

const HEARTBEAT_MS = 1000;

export function installEngagementTracker(opts: EngagementTrackerOptions): EngagementTracker {
  let engagedMs = 0;
  let lastTickAt = Date.now();
  let isVisible = document.visibilityState === "visible";
  let isActive = true;
  let lastActivityAt = Date.now();
  let timer: ReturnType<typeof setInterval> | null = null;
  let emitted = false;

  const IDLE_AFTER_MS = 30_000;

  function tick() {
    const now = Date.now();
    const delta = now - lastTickAt;
    lastTickAt = now;
    if (isVisible && isActive && now - lastActivityAt < IDLE_AFTER_MS) {
      engagedMs += delta;
    }
  }

  function onActivity() {
    lastActivityAt = Date.now();
    isActive = true;
  }

  function onVisibilityChange() {
    tick();
    isVisible = document.visibilityState === "visible";
    if (!isVisible) emitOnce("visibility");
  }

  function emitOnce(_reason: "visibility" | "spa") {
    tick();
    if (emitted) return;
    emitted = true;
    opts.emit("page_engagement", {
      engaged_time_ms: engagedMs,
      max_scroll_depth: opts.getMaxScrollDepth(),
    });
  }

  timer = setInterval(tick, HEARTBEAT_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
  document.addEventListener("mousemove", onActivity, { passive: true });
  document.addEventListener("keydown", onActivity, { passive: true });
  document.addEventListener("scroll", onActivity, { passive: true, capture: true });
  document.addEventListener("touchstart", onActivity, { passive: true });
  window.addEventListener("pagehide", () => emitOnce("visibility"));

  return {
    destroy(): void {
      if (timer) clearInterval(timer);
      timer = null;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      document.removeEventListener("mousemove", onActivity);
      document.removeEventListener("keydown", onActivity);
      document.removeEventListener("scroll", onActivity, { capture: true } as EventListenerOptions);
      document.removeEventListener("touchstart", onActivity);
    },
    emitNow(reason): void {
      emitOnce(reason);
    },
    getEngagedMs(): number {
      tick();
      return engagedMs;
    },
    reset(): void {
      engagedMs = 0;
      lastTickAt = Date.now();
      lastActivityAt = Date.now();
      emitted = false;
    },
  };
}
